/**
 * useSessionReason - the Lookup page's session-level reason-for-access store (rev4, grown for row 418).
 *
 * Clone of the Portal's useReasonForAccess zustand store (v1/src/state/useReasonForAccess.ts),
 * implemented on React's useSyncExternalStore so no new dependency is added: one module-level GRANT
 * that survives SPA navigation and is lost on reload (deliberate - a fresh tab re-asks). The reason
 * is asked ONCE per session (>= 4 chars, the server-side gate on every PHI route) and passed to
 * every roster read, reveal and export; it is persisted into the fail-closed audit server-side.
 *
 * ROW 418 (William, 19 Sep 2026: "make this lookup just like the one for the main portal"): the grant
 * now carries the Portal's SCOPE beside the reason (member, roster_slice, full_reveal, export,
 * cross_tenant) and the moment it was given. The scope rides every lookup call's body and lands in
 * the audit payload (informational: the server's gate is the reason, unchanged). `useSessionReason()`
 * still answers the reason string, so every existing caller reads as before; `useSessionGrant()`
 * answers the whole grant for the banner.
 *
 * An EXPORT never uses this grant's scope: the Portal re-prompts for an export-scoped reason on every
 * export and runs the export with that fresh reason without disturbing the entry grant. The page
 * does the same (Lookup.tsx `exportProceed`); the export reason travels as an argument, never through
 * this store.
 */
import { useSyncExternalStore } from 'react';

export const MIN_REASON_CHARS = 4;

/** The Portal's closed vocabulary (useReasonForAccess.ts `Scope`), verbatim. */
export type ReasonScope = 'member' | 'roster_slice' | 'full_reveal' | 'export' | 'cross_tenant';

export const REASON_SCOPES: ReadonlyArray<{ value: ReasonScope; label: string }> = [
  { value: 'member', label: 'Single member (full PHI reveal)' },
  { value: 'roster_slice', label: 'Roster slice (filtered cohort)' },
  { value: 'full_reveal', label: 'Full PHI reveal (multi-member)' },
  { value: 'export', label: 'Export (PHI in artifact)' },
  { value: 'cross_tenant', label: 'Cross-tenant (super-admin only)' },
];

/** The scope the Lookup's entry gate asks for (every tab is a roster-level browse); full_reveal also satisfies it. */
export const ENTRY_SCOPE: ReasonScope = 'roster_slice';
export const ENTRY_SCOPES: ReadonlyArray<ReasonScope> = ['roster_slice', 'full_reveal'];

export interface SessionGrant {
  reason: string;
  scope: ReasonScope | null;
  /** ISO time the grant was given in this tab, '' when none. */
  mintedAt: string;
}

const EMPTY: SessionGrant = { reason: '', scope: null, mintedAt: '' };
let grant: SessionGrant = EMPTY;
const listeners = new Set<() => void>();

function emit(next: SessionGrant): void {
  grant = next;
  for (const l of listeners) l();
}

/** Give the grant with a scope (the modal's path). */
export function setSessionGrant(scope: ReasonScope, reason: string): void {
  const next = reason.trim();
  if (next === grant.reason && scope === grant.scope) return;
  emit({ reason: next, scope, mintedAt: next ? new Date().toISOString() : '' });
}

/** The pre-row-418 setter, kept for its callers: a bare reason is the entry scope. */
export function setSessionReason(reason: string): void {
  const next = reason.trim();
  if (next === grant.reason) return;
  emit(next ? { reason: next, scope: ENTRY_SCOPE, mintedAt: new Date().toISOString() } : EMPTY);
}

export function clearSessionReason(): void {
  if (grant === EMPTY) return;
  emit(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getReason(): string {
  return grant.reason;
}

function getGrant(): SessionGrant {
  return grant;
}

/** The current session reason ('' until set). Valid when length >= MIN_REASON_CHARS. */
export function useSessionReason(): string {
  return useSyncExternalStore(subscribe, getReason, getReason);
}

/** The whole grant (reason, scope, when), for the banner. */
export function useSessionGrant(): SessionGrant {
  return useSyncExternalStore(subscribe, getGrant, getGrant);
}

/** The scope in force right now, for a call body (null before the grant). Not a hook. */
export function currentScope(): ReasonScope | null {
  return grant.scope;
}

export function isValidReason(reason: string): boolean {
  return reason.trim().length >= MIN_REASON_CHARS;
}

/** True when the grant opens the Lookup: a valid reason under an entry scope (or the full reveal). */
export function grantOpensLookup(g: SessionGrant): boolean {
  return isValidReason(g.reason) && g.scope !== null && ENTRY_SCOPES.includes(g.scope);
}
